import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
} from 'react';
import styled, { css, createGlobalStyle } from 'styled-components';

import Message from 'antd/lib/message';

// import { stringify as stringifyQuery } from 'querystring';

import {
  AppstoreFilled,
  ExpandOutlined,
  FontSizeOutlined,
  AlignLeftOutlined,
  PictureOutlined,
  SelectOutlined,
  GlobalOutlined,
  // ProfileOutlined,
  // SplitCellsOutlined,
  // SendOutlined,
  // PushpinOutlined,
  // BlockOutlined,
  ColumnWidthOutlined,
  BorderOutlined,
  ExclamationCircleOutlined,
  ToTopOutlined,
  PicCenterOutlined,
  VerticalAlignMiddleOutlined,
} from '@ant-design/icons';

import AntdInput from 'antd/lib/input';
import AntdButton from 'antd/lib/button';
import AntdRadio from 'antd/lib/radio';
import AntdDropdown from 'antd/lib/dropdown';
import AntdMenu from 'antd/lib/menu';
import AntdModal from 'antd/lib/modal';
import AntdIcon from 'antd/lib/icon';

import Admin from 'hive-admin';
import Query from 'hive-admin/src/components/Query';
// eslint-disable-next-line max-len
import InfiniteScrollQuery from 'hive-admin/src/components/PageArchive/InfiniteScrollQuery';

// import useQuery from '../../../modules/useQuery';
import { getI18nFields } from '../../../helpers/i18n';

import Types from '../../../modules/types';

import { getSidebarField } from '../../ActionSidebar';

import frameFields, { sidebarFields as frameSidebarFields } from './frame';
import revealFields from './reveal';
import parallaxFields from './parallax';
import textFields from './text';
import textBlockFields from './textBlock';
import buttonFields from './button';
import linkFields from './link';
import mediaFields from './media';
import sliderFields from './slider';
import productsFields from './products';
import iframeFields from './iframe';
import toggleFields from './toggle';

import legacyContentFields from './legacy_content';
import legacySliderFields from './legacy_slider';
import legacyCardsFields from './legacy_cards';
import legacyStatsFields from './legacy_stats';
import legacyButtonFields from './legacy_button';

const {
  CONTENT_BUILDER_ITEM: {
    node: NODE_TYPE,
    TYPE_ITEMS: ITEMS,
    TYPE_LABELS_MAP: LABELS_MAP,
  },
} = Types;

const FIELDS_ESSENTIAL = [
  ...[
    'type',
    'name',
    'hide',
    'id',
    'className',
    'hoverTarget',
  ].map(name => Admin.compileFromLibrary(['FieldHidden', {
    name,
    label: null,
  }])),
];

const FIELDS = [
  ...FIELDS_ESSENTIAL,
  ...frameFields,
  ...revealFields,
  ...parallaxFields,
  ...textFields,
  ...textBlockFields,
  ...linkFields,
  ...buttonFields,
  ...mediaFields,
  ...sliderFields,
  ...productsFields,
  ...iframeFields,
  ...toggleFields,

  ...legacyContentFields,
  ...legacySliderFields,
  ...legacyCardsFields,
  ...legacyStatsFields,
  ...legacyButtonFields,
];

const SIDEBAR_FIELDS_ESSENTIAL_BEFORE = [
  ['FieldHidden', {
    name: 'type',
    label: null,
  }],
  ...getSidebarField.section(
    {
      name: 'hide_cb_sidebar_general',
      title: 'General',
      subtitle: 'Basic identification and categorization settings.',
      // help: 'Configure fundamental attributes like the name, ID, class, and tag for your website elements, ensuring proper identification and categorization.',
      initialValue: true,
    },
    [
      ...getSidebarField.block(
        null,
        [
          [
            ['FieldText', {
              label: null,
              name: 'name',
              placeholder: 'Name',
            }],
          ],
          [
            ['FieldText', {
              label: null,
              name: 'id',
              placeholder: 'ID',
            }],
            ['FieldText', {
              label: null,
              name: 'className',
              placeholder: 'Class',
            }],
          ],
          [
            ['FieldText', {
              label: null,
              name: 'htmlTag',
              placeholder: 'Tag',
              preSkip: [
                props => props.supportsHtmlTag !== true,
              ],
            }],
          ],
          [
            ['FieldText', {
              label: null,
              name: 'hoverTarget',
              placeholder: 'Hover Target',
              preSkip: [
                props => props.supportsHoverTarget !== true,
              ],
            }],
          ],
        ],
      ),
    ],
  ),
];

const SIDEBAR_FIELDS_ESSENTIAL_AFTER = [
  ...getSidebarField.section(
    {
      name: 'hide_cb_sidebar_advanced',
      title: 'Advanced',
      subtitle: 'Custom CSS for advanced styling and behavior.',
      // help: 'Unlock more sophisticated customization by utilizing custom CSS through stitches, granting you greater control over the styling and behavior of your website elements beyond the standard options provided in the other sections.',
      initialValue: true,
    },
    [
      ...getSidebarField.block(
        'Stitches CSS',
        [
          [
            ['FieldCodeEditor', {
              label: null,
              placeholder: '',
              name: 'stitchesCss',
              defaultLanguage: 'json',
              initialValue: '',
              minHeight: '600px',
              getMonacoEditorOptions: () => ({
                scrollBeyondLastLine: false,
                scrollbar: {
                  alwaysConsumeMouseWheel: false,
                },
                minimap: {
                  enabled: false,
                },
                contextmenu: false,
                tabSize: 2,
                insertSpaces: true,
                detectIndentation: false,
                lineNumbers: 'off',
                glyphMargin: false,
                folding: false,
                // Undocumented see https://github.com/Microsoft/vscode/issues/30795#issuecomment-410998882
                lineDecorationsWidth: 0,
                lineNumbersMinChars: 0,
              }),
              prepareValueForInput: (value) => {
                if (typeof value !== 'string') {
                  try {
                    value = JSON.stringify(value, null, '  ');
                  } catch (error) {
                    // eslint-disable-next-line no-console
                    console.log(error);
                    value = '';
                  }
                }
                return value;
              },
            }],
          ],
        ],
      ),
    ],
  ),
];

const SIDEBAR_FIELDS = [
  ...SIDEBAR_FIELDS_ESSENTIAL_BEFORE,
  ...frameSidebarFields,
  ...SIDEBAR_FIELDS_ESSENTIAL_AFTER,
];

const ICONS_MAP = {
  FRAME: ExpandOutlined,
  REVEAL: ToTopOutlined,
  PARALLAX: PicCenterOutlined,
  TEXT: FontSizeOutlined,
  TEXT_BLOCK: AlignLeftOutlined,
  BUTTON: SelectOutlined,
  MEDIA: PictureOutlined,
  SLIDER: ColumnWidthOutlined,
  PRODUCTS: BorderOutlined,
  IFRAME: GlobalOutlined,
  TOGGLE: VerticalAlignMiddleOutlined,

  LINK: SelectOutlined,

  LEGACY_CARDS: BorderOutlined,
  LEGACY_SLIDER: ColumnWidthOutlined,

  LEGACY_CONTENT: ExclamationCircleOutlined,
  LEGACY_STATS: ExclamationCircleOutlined,
  LEGACY_BUTTON: ExclamationCircleOutlined,

  '*': AppstoreFilled,
};

function useIsMountedRef() {
  const isMountedRef = useRef(false);
  useEffect(
    () => {
      isMountedRef.current = true;
      return () => {
        useIsMountedRef.current = false;
      }
    },
    [],
  );
  return isMountedRef;
}

const GlobalModalLoadingStyle = createGlobalStyle`
  ${({ $loading }) => $loading && css`
    body * {
      pointer-events: none !important;
    }
  `}
`;

const StyledModalHTML = ({ className, ...props }) => (
  <AntdModal
    {...props}
    wrapClassName={className}
  />
);

const StyledModal = styled(StyledModalHTML)`
  .ant-modal-body {
    padding: 0px;
  }
`;

const ModalWrapper = styled.div`
`;

const AddSegmentButtonMenuWrapper = styled.div`
  /* padding: 2px; */
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1);
  .ant-dropdown-menu {
    box-shadow: none;
    padding: 0px;
    margin: 0px;
  }
`;

const AddSegmentButtonMenuController = React.forwardRef(
  (
    {
      children,
      style,
      header = [],
      ...props
    },
    overlayRef,
  ) => (
    <AddSegmentButtonMenuWrapper style={style}>
      {header || null}
      {
        !children
        ? null
        : React.cloneElement(children, { ref: overlayRef, ...props })
      }
    </AddSegmentButtonMenuWrapper>
  ),
);

const InfiniteScrollQueryForwardRef = React.forwardRef(
  (
    {
      children,
      infiniteScrollQueryProps,
      infiniteScrollQueryScrollWrapperStyle = {},
      ...props
    },
    overlayRef,
  ) => (
    <div style={infiniteScrollQueryScrollWrapperStyle}>
      <InfiniteScrollQuery {...infiniteScrollQueryProps}>
        {
          (queryProps) => children({
            ...queryProps,
            initialProps: props,
            overlayRef,
          })
        }
      </InfiniteScrollQuery>
    </div>
  ),
);

const AddSegmentButtonMenuSearch = React.forwardRef(
  ({ children, ...props }, _parentRef) => {
    const ref = useRef();
    React.useEffect(
      () => {
        ref?.current?.focus?.();
      },
      [ref],
    );
    return (
      <AntdInput
        {...props}
        style={{
          borderBottomRightRadius: '0px',
          borderBottomLeftRadius: '0px',
        }}
        ref={ref}
      />
    );
  },
);

export const AddSegmentButton = ({
  label,
  items,
  types,
  extraQueryConditions = {},
  itemsList,
  onAddItem,
  props,
  storeId,
}) => {
  const wasVisibleRef = useRef(false);
  const [isVisible, setIsVisible] = useState(false);
  const [search, setSearch] = useState('');
  const searchRegExp = new RegExp(search?.length ? search : '.?', 'i');
  const itemsListLabelsOverridesMap = useMemo(
    () => (
      itemsList?.reduce?.(
        (agr, item) => {
          agr[item.segment] = item.label;
          return agr;
        },
        {},
      ) || {}
    ),
    [itemsList],
  );
  if (itemsList?.length === 1) {
    const queryFinal = {
      AND: [{
        ...extraQueryConditions,
        ...(
          itemsList?.length
          ? {
              _id: {
                IN: itemsList.map(
                  ({ segment }) => segment || '__NO_VALUE__'
                ),
              },
            }
          : { type: '__NO_VALUE__' }
        ),
      }, {
        OR: types.map(type => ({
          type: (
            type === 'BLOCK' && !storeId
            ? '__NO_VALUE__'
            : type
          ),
          store: type === 'BLOCK' ? storeId : undefined,
        })),
      }],
    };
    return (
      <Query
        url={`${props.segmentsUrl}?query=${encodeURIComponent(JSON.stringify({
          where: queryFinal,
          limit: 1,
        }))}`}
        method="GET"
        client={props.client}
      >
        {({ data }) => {
          const item = data?.data?.data?.[0];
          if (item) {
            return (
              <AntdButton
                onClick={() => onAddItem({
                  ...item,
                  label: itemsListLabelsOverridesMap[
                    item._id
                  ],
                })}
                icon="plus"
              >
                {label}
              </AntdButton>
            );
          }
          return (
            <AntdButton
              disabled
              icon="plus"
            >
              {label}
            </AntdButton>
          );
        }}
      </Query>
    );
  }
  return (
    <AntdDropdown
      destroyPopupOnHide
      onVisibleChange={newVisible => {
        if (newVisible) {
          wasVisibleRef.current = true;
        }
        setIsVisible(newVisible);
      }}
      overlay={(
        <AddSegmentButtonMenuController
          header={(
            <AddSegmentButtonMenuSearch
              value={search}
              placeholder="Search"
              onChange={({ target: { value } }) => setSearch(value || '')}
            />
          )}
        >
          {
            items?.length
            ? (
                <AntdMenu
                  style={{ height: '200px', overflowY: 'scroll' }}
                >
                  {
                    items
                    .filter(item => (
                      searchRegExp.test(item.label) ? true : false
                    ))
                    .map((item) => (
                      <AntdMenu.Item
                        key={item._id || item.id}
                        value={item}
                        onClick={() => onAddItem(item)}
                      >
                        {
                          itemsListLabelsOverridesMap[item._id || item.id]
                          || item.name || item.label
                        }
                      </AntdMenu.Item>
                    ))
                  }
                </AntdMenu>
              )
            : !isVisible
            ? null
            : (
                <InfiniteScrollQueryForwardRef
                  infiniteScrollQueryScrollWrapperStyle={{
                    height: '200px',
                    overflowY: 'scroll',
                  }}
                  infiniteScrollQueryProps={{
                    url: props.segmentsUrl,
                    method: 'GET',
                    client: props.client,
                    noInfiniteScrollElement: true,
                    extractData: response => response?.data || null,
                    limit: 10,
                    search: itemsList?.length ? undefined : search,
                    getExtraQueryProps: () => ({ catalogue: true }),
                    getWhereConditions: (queryProps) => {
                      const itemsListSearched = (
                          itemsList?.length
                        ? (
                            itemsList
                            .filter(item => searchRegExp.test(item.label))
                          )
                        : null
                      );
                      const queryFinal = {
                        AND: [{
                          name: (
                            queryProps.search?.length
                            ? { REGEX: queryProps.search, OPTIONS: 'i' }
                            : undefined
                          ),
                          ...extraQueryConditions,
                          ...(
                            itemsList?.length
                            ? (
                                itemsListSearched?.length
                                ? {
                                    _id: {
                                      IN: itemsListSearched.map(
                                        ({ segment }) => (
                                          segment
                                          || '__NO_VALUE__'
                                        )
                                      ),
                                    },
                                  }
                                : { type: '__NO_VALUE__' }
                              )
                            : {}
                          ),
                        }, {
                          OR: types.map(type => ({
                            type: (
                              type === 'BLOCK' && !storeId
                              ? '__NO_VALUE__'
                              : type
                            ),
                            store: type === 'BLOCK' ? storeId : undefined,
                          })),
                        }],
                      };
                      return queryFinal;
                    },
                    shouldReload: (oldProps, newProps) => (
                      oldProps.search !== newProps.search
                    ),
                  }}
                >
                  {({ data, initialProps, overlayRef }) => (
                    <AntdMenu
                      {...initialProps}
                      ref={overlayRef}
                    >
                      {
                          itemsList?.length
                        ? (itemsMap => (
                            itemsList.reduce(
                              (agr, { segment }) => {
                                const item = itemsMap[segment];
                                if (item) {
                                  agr.push(
                                    <AntdMenu.Item
                                      key={item._id}
                                      value={item}
                                      onClick={() => onAddItem({
                                        ...item,
                                        label: itemsListLabelsOverridesMap[
                                          item._id
                                        ],
                                      })}
                                    >
                                      {
                                        itemsListLabelsOverridesMap[
                                          item._id
                                        ]
                                        || item.name
                                        || item.label
                                      }
                                    </AntdMenu.Item>
                                  );
                                }
                                return agr;
                              },
                              [],
                            )
                          ))(
                            data?.data?.reduce?.(
                              (agr, item) => {
                                agr[item._id] = item;
                                return agr;
                              },
                              {},
                            ) || {}
                          )
                        : data?.data?.map?.((item) => (
                            <AntdMenu.Item
                              key={item._id}
                              value={item}
                              onClick={() => onAddItem({
                                ...item,
                                label: itemsListLabelsOverridesMap[item._id],
                              })}
                            >
                              {
                                itemsListLabelsOverridesMap[item._id]
                                || item.name
                              }
                            </AntdMenu.Item>
                          ))
                      }
                    </AntdMenu>
                  )}
                </InfiniteScrollQueryForwardRef>
              )
          }
        </AddSegmentButtonMenuController>
      )}
    >
      <AntdButton icon="plus">
        {label}
      </AntdButton>
    </AntdDropdown>
  );
};

const AddSegmentButtonWithModalHeader = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const AddSegmentButtonWithModalHeaderTitle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

const AddSegmentButtonWithModalHeaderActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

const AddSegmentButtonWithModalList = styled.div`
  padding: 20px;
`;

const AddSegmentButtonWithModalItemWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  /* cursor: pointer; */
  position: relative;
  border-radius: 10px;
  transition: background 300ms;
  padding: 10px;

  & > * { z-index: 1 };

  /* &:hover {
    background: #f9be4926;
  } */

  &:not(:last-child) {
    margin-bottom: 15px;
  }
`;

const AddSegmentButtonWithModalItemImage = styled.div`
  display: flex;
  position: relative;
  width: 200px;
  height: 200px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 10px;
  overflow: hidden;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  background-image: url("${({ $src }) => $src}");
  align-items: center;
  justify-content: center;
`;

const AddSegmentButtonWithModalItemLoaderIcon = styled(AntdIcon)`
  color: ${({ theme }) => theme.less.primaryColor};
  font-size: 80px;
`;

const AddSegmentButtonWithModalItemLoaderWrapper = styled.div`
  display: flex;
  position: absolute;
  align-items: center;
  justify-content: center;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  pointer-events: none;
  background: rgba(0, 0, 0, 0.04);
  transition: opacity 300ms linear;
  opacity: 0;
  &[data-visible="true"] {
    opacity: 1;
  }
`;

const AddSegmentButtonWithModalItemAttribution = styled.div`
  font-size: 70%;
  /* margin-top: 5px; */
`;

const AddSegmentButtonWithModalItemInfo = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 20px 10px 20px 40px;
  transition: opacity 300ms linear;
  ${({ $loading }) => $loading && css`
    opacity: 0.5;
  `}
`;

const AddSegmentButtonWithModalItemTitle = styled.h1`
  margin-bottom: 0px;
`;

const AddSegmentButtonWithModalItemDescription = styled.p`
  opacity: 0.75;
`;

const AddSegmentButtonWithModalItemActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  margin-top: 5px;
  vertical-align: middle;
`;

const AddSegmentButtonWithModalItemActionsDivider = styled.div`
  display: flex;
  width: 1px;
  height: 30px;
  background-color: ${({ theme }) => theme.less.textColor};
  opacity: 0.5;
`;

const AddSegmentButtonWithModalTabLabel = styled.span`
  font-weight: 400;
`;

// eslint-disable-next-line arrow-body-style
const AddSegmentButtonWithModalItem = ({
  item = {},
  label,
  onAddItem,
  client,
  storeId,
}) => {
  const isMountedRef = useIsMountedRef();
  const [isLoading, setIsLoading] = useState(false);
  const loadStoreStyleAction = useMemo(
    () => {
      const action = Admin.compileFromLibrary(['ActionWithRequest', {
        client,
        name: 'actionLoadPreviewStoreStyle',
        title: 'Load Style',
        size: 'small',
        ghost: true,
        // eslint-disable-next-line no-unused-vars
        getRequestConfig: props => ({
          url: `stores/${storeId}/load-store-style`,
          method: 'POST',
          data: { fromStore: item.previewStore },
        }),
        skip: [
          () => !storeId,
        ],
        popconfirm: {
          title: (
            <>
              Overwrite style settings of your store with the values found
              in the template preview?
              <br />
              <br />
              This action is non reversable!
            </>
          ),
          okText: 'Load',
        },
      }]);
      return action;
    },
    [client, storeId, item.previewStore],
  );
  return (
    <AddSegmentButtonWithModalItemWrapper>
      <AddSegmentButtonWithModalItemImage
        $src={item.image?.src}
      >
        <AddSegmentButtonWithModalItemLoaderWrapper
          data-visible={isLoading}
        >
          <AddSegmentButtonWithModalItemLoaderIcon
            type="loading"
            spin
          />
        </AddSegmentButtonWithModalItemLoaderWrapper>
      </AddSegmentButtonWithModalItemImage>
      <AddSegmentButtonWithModalItemInfo
        $loading={isLoading}
      >
        <AddSegmentButtonWithModalItemTitle>
          {label || item.name}
        </AddSegmentButtonWithModalItemTitle>
        {
          item.description
          ? (
              <AddSegmentButtonWithModalItemDescription>
                {item.description}
              </AddSegmentButtonWithModalItemDescription>
            )
          : null
        }
        <AddSegmentButtonWithModalItemActions>
          <AntdButton
            type="primary"
            size="small"
            onClick={() => {
              setIsLoading(true);
              onAddItem(false, () => {
                if (isMountedRef.current) {
                  setIsLoading(false);
                }
              });
            }}
          >
            Add
          </AntdButton>
          {
            item.fields?.length && storeId
            ? (
                <AntdButton
                  type="primary"
                  size="small"
                  onClick={() => {
                    setIsLoading(true);
                    onAddItem(true, () => setIsLoading(false));
                  }}
                >
                  Generate With AI
                </AntdButton>
              )
            : null
          }
          {
            item.previewLink || item.previewStore
            ? (
                <>
                  <AddSegmentButtonWithModalItemActionsDivider />
                  {
                    item.previewLink
                    ? (
                        <AntdButton
                          type="primary"
                          ghost
                          size="small"
                          href={item.previewLink}
                          target="_blank"
                        >
                          Preview
                        </AntdButton>
                      )
                    : null
                  }
                  {
                    item.previewStore
                    ? (
                        loadStoreStyleAction.render({
                          client,
                        })
                      )
                    : null
                  }
                </>
              )
            : null
          }
        </AddSegmentButtonWithModalItemActions>
      </AddSegmentButtonWithModalItemInfo>
    </AddSegmentButtonWithModalItemWrapper>
  );
};

export const AddSegmentButtonWithModal = ({
  label,
  types,
  extraQueryConditions: extraQueryConditionsInitial = {},
  itemsList,
  onAddItem,
  props,
  defaultIsOpen = false,
  defaultSegmentKind = 'ELEMENT',
  defaultIsInternal = false,
  storeId,
}) => {
  const [isOpen, setIsOpen] = useState(defaultIsOpen);
  const [isLoading, setIsLoading] = useState(false);
  const [segmentKind, setSegmentKind] = useState(defaultSegmentKind);
  const [isInternal, setIsInternal] = useState(defaultIsInternal);
  const [search, setSearch] = useState('');
  const searchRegExp = new RegExp(search?.length ? search : '.?', 'i');
  const extraQueryConditions = useMemo(
    () => ({
      ...extraQueryConditionsInitial,
      internal: isInternal ? true : { NE: true },
    }),
    [extraQueryConditionsInitial, isInternal],
  );
  const itemsListLabelsOverridesMap = useMemo(
    () => (
      itemsList?.reduce?.(
        (agr, item) => {
          agr[item.segment] = item.label;
          return agr;
        },
        {},
      ) || {}
    ),
    [itemsList],
  );
  return (
    <>
      <AntdButton
        icon="plus"
        onClick={() => setIsOpen(true)}
      >
        {label}
      </AntdButton>
      <GlobalModalLoadingStyle
        $loading={isLoading}
      />
      <StyledModal
        destroyOnClose
        closable={false}
        centered
        width="80vw"
        $loading={isLoading}
        title={(
          <AddSegmentButtonWithModalHeader>
            <AddSegmentButtonWithModalHeaderTitle>
              {label}
              <AntdInput
                autoFocus
                placeholder="Search"
                align="left"
                style={{
                  textAlign: 'left',
                  marginRight: '10px',
                  maxWidth: '150px',
                }}
                value={search}
                onChange={({ target: { value } }) => setSearch(value)}
              />
            </AddSegmentButtonWithModalHeaderTitle>
            <AddSegmentButtonWithModalHeaderActions>
              {
                props.viewer?.role === 'ADMIN'
                ? (
                    <AntdRadio.Group
                      value={isInternal}
                      onChange={event => setIsInternal(event.target.value)}
                    >
                      {
                        [
                          ['Public', false],
                          ['Internal', true],
                        ].map(([internalLabel, internalValue]) => (
                          <AntdRadio.Button
                            key={internalLabel}
                            value={internalValue}
                          >
                            <span
                              style={{
                                fontWeight: 400,
                                ...(
                                  internalValue === isInternal
                                  ? {} : { opacity: 0.75 }
                                ),
                              }}
                            >
                              {internalLabel}
                            </span>
                          </AntdRadio.Button>
                        ))
                      }
                    </AntdRadio.Group>
                  )
                : null
              }
              <AntdRadio.Group
                value={segmentKind}
                onChange={event => setSegmentKind(event.target.value)}
              >
                {
                  [
                    ['ELEMENT', 'Elements'],
                    ['SECTION', 'Sections'],
                    ['TEMPLATE', 'Templates'],
                  ].map(([tabValue, tabLabel]) => (
                    <AntdRadio.Button key={tabValue} value={tabValue}>
                      <AddSegmentButtonWithModalTabLabel
                        style={
                          segmentKind === tabValue
                          ? undefined : { opacity: 0.75 }
                        }
                      >
                        {tabLabel}
                      </AddSegmentButtonWithModalTabLabel>
                    </AntdRadio.Button>
                  ))
                }
              </AntdRadio.Group>
            </AddSegmentButtonWithModalHeaderActions>
          </AddSegmentButtonWithModalHeader>
        )}
        visible={isOpen}
        footer={(
          <AddSegmentButtonWithModalItemAttribution>
            <a
              href="https://www.pexels.com"
              target="_blank"
              rel="noreferrer"
            >
              Photos provided by Pexels ❤️
            </a>
          </AddSegmentButtonWithModalItemAttribution>
        )}
        onCancel={() => setIsOpen(false)}
      >
        <ModalWrapper>
          <InfiniteScrollQueryForwardRef
            infiniteScrollQueryScrollWrapperStyle={{
              minHeight: 'calc(80vh - 70px - 38px)',
              maxHeight: 'calc(80vh - 70px - 38px)',
              overflowY: 'scroll',
            }}
            infiniteScrollQueryProps={{
              url: props.segmentsUrl,
              method: 'GET',
              client: props.client,
              noInfiniteScrollElement: true,
              extractData: response => response?.data || null,
              limit: 10,
              search,
              segmentKind,
              isInternal,
              getExtraQueryProps: () => ({ catalogue: true }),
              getWhereConditions: (queryProps) => {
                const itemsListSearched = (
                    itemsList?.length
                  ? (
                      itemsList
                      .filter(item => searchRegExp.test(item.label))
                    )
                  : null
                );
                const queryFinal = {
                  AND: [{
                    kind: segmentKind,
                    name: (
                      queryProps.search?.length
                      ? { REGEX: queryProps.search, OPTIONS: 'i' }
                      : undefined
                    ),
                    ...extraQueryConditions,
                    ...(
                      itemsList?.length
                      ? (
                          itemsListSearched?.length
                          ? {
                              _id: {
                                IN: itemsListSearched.map(
                                  ({ segment }) => segment || '__NO_VALUE__'
                                ),
                              },
                            }
                          : { type: '__NO_VALUE__' }
                        )
                      : {}
                    ),
                  }, {
                    OR: types.map(type => ({
                      type: (
                        type === 'BLOCK' && !storeId
                        ? '__NO_VALUE__'
                        : type
                      ),
                      store: type === 'BLOCK' ? storeId : undefined,
                    })),
                  }],
                };
                return queryFinal;
              },
              shouldReload: (oldProps, newProps) => (
                  oldProps.search !== newProps.search
                || oldProps.segmentKind !== newProps.segmentKind
                || oldProps.isInternal !== newProps.isInternal
              ),
            }}
          >
            {({ data, initialProps, ref }) => (
              <AddSegmentButtonWithModalList
                {...initialProps}
                ref={ref}
              >
                {
                    itemsList?.length
                  ? (itemsMap => (
                      itemsList.reduce(
                        (agr, { segment }) => {
                          segment = segment || '__NO_VALUE__';
                          const item = itemsMap[segment];
                          if (item) {
                            agr.push(
                              <AddSegmentButtonWithModalItem
                                key={item._id}
                                item={item}
                                label={
                                  itemsListLabelsOverridesMap[item._id]
                                  || item.name
                                }
                                client={props.client}
                                onAddItem={(generate = false, cb) => {
                                  setIsLoading(true);
                                  onAddItem(
                                    {
                                      ...item,
                                      label: itemsListLabelsOverridesMap[
                                        item._id
                                      ],
                                    },
                                    generate,
                                    () => {
                                      setIsLoading(false);
                                      setIsOpen(false);
                                      cb && cb();
                                    },
                                  );
                                }}
                                storeId={storeId}
                              />
                            );
                          }
                          return agr;
                        },
                        [],
                      )
                    ))(
                      data?.data?.reduce?.(
                        (agr, item) => {
                          agr[item._id] = item;
                          return agr;
                        },
                        {},
                      ) || {}
                    )
                  : data?.data?.map?.((item) => (
                      <AddSegmentButtonWithModalItem
                        key={item._id}
                        item={item}
                        label={
                          itemsListLabelsOverridesMap[item._id]
                          || item.name
                        }
                        client={props.client}
                        onAddItem={(generate = false, cb) => {
                          setIsLoading(true);
                          onAddItem(
                            {
                              ...item,
                              label: itemsListLabelsOverridesMap[item._id],
                            },
                            generate,
                            () => {
                              setIsLoading(false);
                              setIsOpen(false);
                              cb && cb();
                            },
                          );
                        }}
                        storeId={storeId}
                      />
                    ))
                }
              </AddSegmentButtonWithModalList>
            )}
          </InfiniteScrollQueryForwardRef>
        </ModalWrapper>
      </StyledModal>
    </>
  );
};

export const AfterAddButtonWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
`;

export const AfterAddButton = ({
  field,
  props,
  getButtons,
}) => {
  const propsRef = useRef(props);
  propsRef.current = props;
  // eslint-disable-next-line no-unused-vars
  const { isAdmin, isTheHiveAdmin } = useMemo(
    () => {
      const result = { isAdmin: false, isTheHiveAdmin: false };
      if (props.viewer?.role === 'ADMIN') {
        result.isAdmin = true;
        if (/@thehive\.rs$/.test(props.viewer.email)) {
          result.isTheHiveAdmin = true;
        }
      }
      return result;
    },
    [props.viewer],
  );
  const form = useMemo(
    () => (
        props.forms?.length
      ? props.forms[props.forms.length - 1]
      : props.form
    ),
    [props.forms, props.form],
  );
  const rootData = (
      props.datas
    ? props.datas[props.datas.length - 1]
    : props.data
  )
  const store = (
    props.viewer?.store
    || rootData?.store
    || (form ? form.getFieldValue('store') : null)
  );
  const handleAddItem = useCallback(
    (item, generate = false, cb) => {
      switch (item.type) {
        case 'COMPONENT':
          propsRef.current.client.get(
            `/contentbuilder/segments/${item._id}`,
          ).then(({ data: componentItem }) => {
            if (componentItem) {
              field.addItem({
                ...propsRef.current.generateNewItem(
                  propsRef.current, field, 'COMPONENT',
                ),
                name: item.label,
                label: componentItem.name || 'Missing Component',
                component_id: componentItem._id,
                component_fields: componentItem.fields || [],
              });
            }
          }).catch((error) => {
            const msg = 'Error adding component:';
            // eslint-disable-next-line no-console
            console.log(msg, error);
            Message.error(`${msg} ${
              error.response?.data?.message
              || error.message
            }`);
          }).then(() => cb && cb());
        break;
        case 'BLOCK':
          field.addItem({
            ...propsRef.current.generateNewItem(
              propsRef.current, field, 'BLOCK',
            ),
            name: item.label,
            label: item.name || 'Missing Block',
            block_id: item._id,
          });
          cb && cb();
        break;
        case 'LIBRARY_ITEM':
          propsRef.current.client.get(
              generate
            ? `/contentbuilder/segments/${item._id}/generate?store=${
                store
              }`
            : `/contentbuilder/segments/${item._id}?resolveContent=true`
          ).then(({ data: libraryItem }) => {
            if (libraryItem?.content?.length) {
              field.addItem(...libraryItem.content);
            }
          }).catch((error) => {
            const msg = `Error ${
              generate
              ? 'generating'
              : 'getting'
            } library item:`;
            // eslint-disable-next-line no-console
            console.log(msg, error);
            Message.error(`${msg} ${
              error.response?.data?.message
              || error.message
            }`);
          }).then(() => cb && cb());
        break;
        default:
          field.addItem({
            ...propsRef.current.generateNewItem(
              propsRef.current, field, item.type,
            ),
          });
          cb && cb();
        break;
      }
    },
    [field, store],
  );
  const buttons = useMemo(
    () => {
      const isUsingSegmentsChoices = props.segmentsChoices?.length > 0;
      // const isUsingSegmentsChoicesExclively = (
      //   isUsingSegmentsChoices
      //   && props.segmentsChoicesExclusive === false
      // );
      const showNonSegmentChoicesButtons = (
        !isUsingSegmentsChoices
        || props.segmentsChoicesExclusive === false
      );
      return [{
        id: 'HINT',
        types: ['LIBRARY_ITEM', 'COMPONENT', 'BLOCK'],
        label: props.segmentsChoicesAddButtonLabel || 'Add',
        itemsList: (
            props.segmentsChoices?.length
          ? props.segmentsChoices
          : null
        ),
        show: isUsingSegmentsChoices,
      }, {
        id: 'PRIMITIVE',
        types: ['PRIMITIVE'],
        label: 'Primitive',
        items: props.segmentsPrimitives,
        show: showNonSegmentChoicesButtons && (
          props.canAddPrimitives && isAdmin
        ),
      }, {
        id: 'COMPONENT',
        types: ['COMPONENT'],
        label: 'Component',
        show: showNonSegmentChoicesButtons && (
          props.canAddComponents && isTheHiveAdmin
        ),
      }, {
        id: 'LIBRARY_ITEM',
        types: ['LIBRARY_ITEM'],
        label: 'Library',
        show: showNonSegmentChoicesButtons && (
          true
        ),
        asModal: true,
      }, {
        id: 'BLOCK',
        types: ['BLOCK'],
        label: 'Block',
        show: showNonSegmentChoicesButtons && (
          !!store
        ),
      }]
      .map(
        ({ items, extraQueryConditions = {}, ...rest }) => (
          items
          ? ({ ...rest, items, extraQueryConditions })
          : ({
              ...rest,
              items,
              extraQueryConditions: (
                isAdmin
                ? extraQueryConditions
                : {
                    ...extraQueryConditions,
                    internal: { NE: true },
                  }
              ),
            })
        )
      );
    },
    [
      isAdmin,
      isTheHiveAdmin,
      props.canAddComponents,
      props.canAddPrimitives,
      props.segmentsChoices,
      props.segmentsChoicesAddButtonLabel,
      props.segmentsChoicesExclusive,
      props.segmentsPrimitives,
      store,
    ],
  );

  return (
    getButtons
    ? getButtons(buttons)
    : buttons
  ).map(({
    id,
    types,
    extraQueryConditions,
    itemsListLabelsOverridesMap,
    itemsList,
    label,
    items,
    show,
    asModal,
    defaultSegmentKind,
  }) => (
    !show
    ? null
    : asModal
    ? (
        <AddSegmentButtonWithModal
          key={id}
          types={types}
          extraQueryConditions={extraQueryConditions}
          itemsListLabelsOverridesMap={itemsListLabelsOverridesMap}
          itemsList={itemsList}
          label={label}
          items={items}
          onAddItem={handleAddItem}
          defaultSegmentKind={defaultSegmentKind}
          props={props}
          storeId={store}
        />
      )
    : (
        <AddSegmentButton
          key={id}
          types={types}
          extraQueryConditions={extraQueryConditions}
          itemsListLabelsOverridesMap={itemsListLabelsOverridesMap}
          itemsList={itemsList}
          label={label}
          items={items}
          onAddItem={handleAddItem}
          props={props}
          storeId={store}
        />
      )
  ))
};

const CONFIG = {
  fields: FIELDS,
  sidebarFields: SIDEBAR_FIELDS,
  getItemFields: (item, props) => {
    let returnFields = props.fields || [];
    if (item.type === 'COMPONENT') {
      const fields = item.component_fields || [];
      returnFields = [
        ...FIELDS_ESSENTIAL,
        Admin.compileFromLibrary(['FieldHidden', {
          name: 'label',
          initialValue: item.name,
        }]),
        Admin.compileFromLibrary(['FieldHidden', {
          name: 'component_id',
          initialValue: item.component_id,
        }]),
        Admin.compileFromLibrary(['FieldHidden', {
          name: 'component_data.types',
          initialValue: fields.reduce(
            (agr, field) => {
              agr[field.id || field._id] = (field.fieldType || '');
              return agr;
            },
            {},
          ),
        }]),
        ...Admin.compileFromLibrary(
          fields.map(field => [
            field.fieldType,
            {
              label: null,
              ...(field.fieldConfig || {}),
              transformations: (
                field?.fieldConfig.transformations?.length
                ? field.fieldConfig.transformations
                : [
                    ['GM', { command: 'compress', maxWidth: 2000 }],
                    ['GM', { command: 'webp' }],
                    // TODO Convert video by default
                    // ['FFMPEG', { command: 'compress', maxWidth: 1080 }],
                  ]
              ),
              name: `component_data.values.${field.id || field._id}.value`,
              targetableName: `component_data.values.${
                field.id || field._id
              }.value`,
              i18nGetFieldName: (_field, languageId) => (
                `component_data.values.${
                  field.id || field._id
                }.${
                  languageId === 'en'
                  ? 'value'
                  : `i18n.${languageId}.value`
                }`
              ),
            },
          ]),
          true,
        ),
      ];
    } else if (item.type === 'BLOCK') {
      returnFields = [
        ...FIELDS_ESSENTIAL,
        Admin.compileFromLibrary(['FieldHidden', {
          name: 'label',
          initialValue: item.name,
        }]),
        Admin.compileFromLibrary(['FieldHidden', {
          name: 'block_id',
          initialValue: item.block_id,
        }]),
      ];
    }
    return getI18nFields(item, { ...props, fields: returnFields });
  },
  // eslint-disable-next-line arrow-body-style
  getSegmentsQuery: (query, props, type, search = '') => {
    const form = (
        props.forms
      ? props.forms[props.forms.length - 1]
      : props.form
    );
    const rootData = (
        props.datas
      ? props.datas[props.datas.length - 1]
      : props.data
    );
    const store = (
      props.viewer?.store
      || rootData?.store
      || (form ? form.getFieldValue('store') : null)
    );
    const queryFinal = {
      ...(query || {}),
      where: {
        type,
        name: search?.length ? { REGEX: search, options: 'i' } : undefined,
        store,
      },
    };
    if (type === 'BLOCK') {
      if (!store) {
        queryFinal.where.type = '__NO_VALUE__';
      }
    } else {
      queryFinal.where.store = undefined;
    }
    return queryFinal;
  },
  renderAddButton: () => null,
  renderAfterAddButton: (props, field) => (
    <AfterAddButtonWrapper>
      <AfterAddButton field={field} props={props} />
    </AfterAddButtonWrapper>
  ),
  // eslint-disable-next-line no-unused-vars
  // getFieldContentBuilderConfig: (field, fieldConfigInitial = {}) => {
  //   const { render } = field;
  //   const {
  //     segmentsQuery,
  //     getSegmentsQuery,
  //     segmentsUrl,
  //     getSegmentsUrl,
  //   } = fieldConfigInitial;
  //   return {
  //     ...field,
  //     render: (props) => {
  //       const original = render(props);
  //       return (
  //         <ContentBuilderSegmentsProvider
  //           client={props.client}
  //           forms={props.forms}
  //           form={props.form}
  //           segmentsQuery={segmentsQuery}
  //           getSegmentsQuery={getSegmentsQuery}
  //           segmentsUrl={segmentsUrl}
  //           getSegmentsUrl={getSegmentsUrl}
  //         >
  //           {original}
  //         </ContentBuilderSegmentsProvider>
  //       );
  //     },
  //   };
  // },
  // eslint-disable-next-line arrow-body-style
  getItemHeaderActions: (actions, index, action) => {
    const currentItem = action.props.value?.[index];
    return [
      ...actions,
      {
        divider: true,
        key: 'frame-divider',
      },
      (() => {
        const onClick = () => {
          if (currentItem) {
            action.onChange(
              Array.isArray(action.props.value)
              ? action.props.value.map((item, itemIndex) => (
                  itemIndex === index
                  ? {
                      ...action.props.generateNewItem(
                        action.props,
                        action,
                        'FRAME',
                      ),
                      frame_items: [currentItem],
                    }
                  : item
                ))
              : [currentItem]
            );
          }
        };
        return {
          key: 'frame',
          label: 'Frame',
          onClick,
          icon: (
            <ExpandOutlined
              type="expand"
              title="Frame"
              onClick={onClick}
            />
          ),
        };
      })(),
    ];
  },
  segmentsUrl: '/contentbuilder/segments',
  segmentsChoices: [],
  segmentsPrimitives: ITEMS.reduce(
    (agr, item) => {
      if (item.hidden) {
        return agr;
      }
      const Icon = ICONS_MAP[item.id];
      agr.push({
        _id: item.id,
        ...item,
        name: (
          <>
            {Icon ? <Icon /> : null}
            {item.label}
          </>
        ),
        nameText: item.label,
      });
      return agr;
    },
    [],
  ),
  nodeType: NODE_TYPE,
  toolbarProps: {
    largeLabel: true,
    getSupportsEffects: type => ['FRAME', 'TEXT'].includes(type),
    getSupportsMediaQueries: _type => true,
    getSupportsLayout: type => type === 'FRAME',
    getSupportsSettings: _type => true,
    getSupportsHtmlTag: type => type !== 'COMPONENT',
    items: ITEMS,
    itemsLabelsMap: LABELS_MAP,
    itemsIconsMap: ICONS_MAP,
  },
  copyRealm: 'ContentBuilder:Item',
  pasteRealm: 'ContentBuilder:Item:**',
  getPasteRealm: (props) => (
    props.segmentsChoices?.length
    ? `ContentBuilder:Item:*:(${
        props.segmentsChoices.map(item => item.segment).join('|')
      })`
    : props.pasteRealm
  ),
  targetable: true,
};

Admin.addToLibrary(
  'FieldContentBuilderItems',
  ({
    toolbarProps = {},
    ...config
  } = {}) => {
    const result = Admin.compileFromLibrary(['FieldContentBuilderNodes', {
      canAddPrimitives: true,
      canAddComponents: true,
      ...CONFIG,
      ...config,
      toolbarProps: {
        ...CONFIG.toolbarProps,
        ...toolbarProps,
      },
    }]);
    result[Admin.LIBRARY_NAME_KEY] = 'FieldContentBuilderItems';
    return result;
  },
);
